// Ported from advisor-3.0/src/auth.js
import { jwtDecode } from 'jwt-decode';
import {
  SESSION_STORAGE_AUTH_TOKEN,
  getSessionStorageItem,
  setSessionStorageItem,
} from 'src/utility/sessionStorage';

interface DecodedToken {
  sub: string;
  exp: number;
}

interface TokenResponse {
  isSuccessful: boolean;
  errorCode?: string;
  errorDescription?: string;
  accessToken: string;
}

const GRANT_TYPE = {
  accessToken: 'access_token',
  authorizationCode: 'authorization_code',
} as const;

type GrantType = (typeof GRANT_TYPE)[keyof typeof GRANT_TYPE];

const authServiceUrl = import.meta.env.VITE_AUTH_SERVICE_URL as string | undefined;
const isDevEnvironment = import.meta.env.DEV;

export const getAuthToken = (): string => {
  return getSessionStorageItem<string>(SESSION_STORAGE_AUTH_TOKEN) || '';
};

export const setAuthToken = (token: string): void => {
  setSessionStorageItem(SESSION_STORAGE_AUTH_TOKEN, token);
};

export const validateToken = async (): Promise<boolean> => {
  if (isDevEnvironment) return true;
  if (!authServiceUrl) return Boolean(getAuthToken());

  const response = await fetch(`${authServiceUrl}/verify`, {
    method: 'POST',
    body: JSON.stringify({ accessToken: getAuthToken() }),
  });

  if (!response.ok) {
    window.location.href = '/ns';
  }

  return response.ok;
};

const tokenRequest = async (token: string, grantType: GrantType): Promise<string> => {
  if (!authServiceUrl) {
    throw new Error('VITE_AUTH_SERVICE_URL is not configured');
  }

  const response = await fetch(`${authServiceUrl}/token`, {
    method: 'POST',
    body: JSON.stringify({ token, grantType }),
  });

  const data = (await response.json()) as TokenResponse;

  if (!data.isSuccessful) {
    throw new Error(`Token request failed: [${data.errorCode}] ${data.errorDescription}`);
  }

  setAuthToken(data.accessToken);
  return data.accessToken;
};

export const refreshToken = async (): Promise<string> =>
  tokenRequest(getAuthToken(), GRANT_TYPE.accessToken);

export const redeemCode = async (code: string): Promise<string> =>
  tokenRequest(code, GRANT_TYPE.authorizationCode);

// Remaining JWT lifetime in seconds (0 if missing/expired)
export const getTokenRemainingTime = (): number => {
  const token = getAuthToken();
  if (!token) return 0;
  if (isDevEnvironment) return 3600;
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    const currentTime = Math.floor(Date.now() / 1000);
    const remaining = decoded.exp - currentTime;
    return remaining > 0 ? remaining : 0;
  } catch (error) {
    console.error('Error decoding token:', error);
    return 0;
  }
};

export const getNameID = (): string => {
  const token = getAuthToken();
  if (!token) return '';
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    return decoded.sub;
  } catch (error) {
    console.error('Error decoding token:', error);
    return '';
  }
};
